import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, Box, makeStyles } from '@material-ui/core';
import { PrimaryButton } from '../utilities/commonUtilities/GenericButtonElements';
import clsx from 'clsx';
import { styleBreakDesktop } from '../utilities/layouts/Globals';
import { getCustomColors } from '../../styles/CustomColors';
import { COLOR_WHITE } from '../utilities/Material';
import { COLOR_GREY } from '../utilities/MaterialWatchman';
import { useCallback } from 'react';

const Colors = getCustomColors();

const useStyles = makeStyles(theme => ({
    dialogPaper: {
        margin: 0,
        maxWidth: 'calc(100% - 20px)',
        backgroundColor: 'transparent',
        width: '100%',
        boxShadow: 'none',
        overflowY: 'initial',
    },
    dialogBox: {
        paddingBottom: theme.spacing(2.5),
        width: '100%',
        margin: '0 auto',
        [styleBreakDesktop(theme)]: {
            maxWidth: '473px',
            width: '473px',
        },
    },
    dialogButton: {
        color: theme.palette.primary.dark,
        width: '100%',
        height: 'auto',
        borderBottom: `1px solid ${COLOR_GREY.main}`,
        padding: theme.spacing(2.5),
        backgroundColor: COLOR_WHITE,
        '&:hover': {
            backgroundColor: COLOR_WHITE,
        },
        '& span': {
            fontSize: theme.spacing(2.25),
            lineHeight: `${theme.spacing(2.25)}px`,
        },
        [styleBreakDesktop(theme)]: {
            minWidth: '145px',
        },
    },
    dialogBackdrop: {
        backgroundColor: Colors.BACKGROUND_COLOUR_WITH_TRANSPARENCY,
    },
    buttonGroup: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: theme.spacing(1.25),
        '& button': {
            fontWeight: 400,
        }
    },
    fileButton: {
        borderRadius: 0,
        borderTopLeftRadius: theme.spacing(1.25),
        borderTopRightRadius: theme.spacing(1.25),
    },
    galleryButton: {
        borderRadius: 0,
    },
    cameraButton: {
        borderRadius: 0,
        borderBottomLeftRadius: theme.spacing(1.25),
        borderBottomRightRadius: theme.spacing(1.25),
    },
}));

const ChatAttachmentModal = ({
    onCancel,
    onAddedPhoto,
    open,
    close,
    onBrowseFile,
    fileRef,
}) => {
    const classes = useStyles();
    const [openCamera, setOpenCamera] = useState(false);

    const onCapture = () => setOpenCamera(true);
    const onCancelCapture = () => setOpenCamera(false);

    return (
        <>
            <Dialog
                open={open}
                onClose={close}
                maxWidth={false}
                style={{ 
                    zIndex: 10000,
                    display: 'grid',
                    justifyContent: 'center',
                    alignContent: 'center',
                    placeContent: 'initial',
                 }}
                classes={{
                    paper: classes.dialogPaper,
                }}
                BackdropProps={{
                    classes: {
                        root: classes.dialogBackdrop,
                    },
                }}
            >
                <Box component="div" display="grid" flexDirection="column" className={classes.dialogBox}>
                    <div className={classes.buttonGroup}>
                        <PrimaryButton
                            onClick={onBrowseFile}
                            className={clsx(classes.dialogButton, classes.fileButton)}
                        >
                            Browse for File
                        </PrimaryButton>
                        <PrimaryButton
                            onClick={onCapture}
                            className={clsx(classes.dialogButton, classes.cameraButton)}
                        >
                            Take a Photo
                        </PrimaryButton>

                        <input ref={fileRef} onChange={onAddedPhoto} type='file' style={{ display: 'none' }} />
                    </div>

                    <PrimaryButton
                        variant="outlined"
                        onClick={onCancel}
                        className={classes.dialogButton}
                    >
                        Cancel
                    </PrimaryButton>
                </Box>
            </Dialog>

            {/* stacks on top of above dialog */}
            <Dialog
                open={openCamera}
                onClose={close}
                style={{ zIndex: 10001 }}>
                <ChatCamera onAddedPhoto={onAddedPhoto} onCancel={onCancelCapture} />
            </Dialog>
        </>
    );
};

/* type checking for props */
ChatAttachmentModal.propTypes = {
    onCancel: PropTypes.func,
    onAddedPhoto: PropTypes.func,
    open: PropTypes.bool,
    close: PropTypes.bool,
    onBrowseFile: PropTypes.func,
    fileRef: PropTypes.object,
};


const useStyles2 = makeStyles(theme => ({
    cameraArea: {
        position: 'fixed',
        height: '100%',
        width: '100%',
        zIndex: 10001,
        top: 0,
        left: 0,
    },
    cameraContainer: {
        position: 'relative',
        width: '100%',
        height: '100%',
        '& canvas': {
            display: 'none',
        },
    },
    cameraControls: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        display: 'flex',
        justifyContent: 'space-around',
        width: '100%',
        padding: theme.spacing(2),
    },
    button: {
        backgroundColor: COLOR_WHITE,
        color: theme.palette.primary.dark,
        width: '40%',
        maxWidth: theme.spacing(15),
        '&:hover': {
            cursor: 'pointer',
            backgroundColor: COLOR_WHITE,
        },
    },
    capturedStrip: {
        position: 'absolute',
        top: 0,
        left: 0,
    },
}));

const ChatCamera = ({ onCancel, onAddedPhoto }) => {
    const videoRef = useRef(null);
    const photoRef = useRef(null);
    const capturePreviewRef = useRef(null);
    const [previewImageDataURI, setPreviewImageDataURI] = useState(null);
    const [cameraActive, setCameraActive] = useState(false);
    const classes = useStyles2();
    const { innerWidth: windowWidth, innerHeight: windowHeight } = window;

    const getVideo = useCallback(async () => {
        try {
            const video = videoRef.current;
            const stream = await navigator.mediaDevices
                .getUserMedia({ video: { width: windowWidth, height: windowHeight } })
            
            video.srcObject = stream;
            video.play();
            setCameraActive(true);
        } catch(e) {
            console.error(e);
        }
    }, [videoRef, windowWidth, windowHeight]);

    const paintToCanvas = () => {
        let video = videoRef.current;
        let photo = photoRef.current;
        let ctx = photo.getContext("2d");

        const width = windowWidth;
        const height = windowHeight;
        photo.width = width;
        photo.height = height;

        return setInterval(() => {
            ctx.drawImage(video, 0, 0, width, height);
        }, 200);
    };

    const takePhoto = () => {
        const photo = photoRef.current;
        const captureprev = capturePreviewRef.current;

        const data = photo.toDataURL("image/jpeg");
        const img = document.createElement("img");
        img.src = data;
        captureprev.insertBefore(img, captureprev.firstChild);

        setPreviewImageDataURI(data);
    };

    const cancelCapture = () => {
        if (videoRef && videoRef.current) {
            setCameraActive(false);
            videoRef.current.srcObject.getTracks()[0].stop();
        }
        onCancel && onCancel();
    }

    const setCaptured = () => {
        onAddedPhoto && onAddedPhoto(previewImageDataURI);
        setPreviewImageDataURI(null);
        cancelCapture();
    }

    const exitPreview = () => {
        const captureprev = capturePreviewRef.current;
        captureprev.removeChild(captureprev.children[0]);
        setPreviewImageDataURI(null);
    }

    useEffect(() => {
        getVideo();
    }, [getVideo]);

    return (
        <div className={classes.cameraArea}>
            <div className={classes.cameraContainer}>
                <video onCanPlay={paintToCanvas} ref={videoRef} />
                <canvas ref={photoRef} />

                <Box className={classes.capturedStrip}>
                    <div ref={capturePreviewRef} />
                </Box>

                <Box className={classes.cameraControls}>
                    {
                        !previewImageDataURI ?
                            <>
                                <PrimaryButton className={classes.button} onClick={takePhoto}>Capture</PrimaryButton>
                                <PrimaryButton className={classes.button} onClick={cancelCapture}>Cancel</PrimaryButton>
                            </> :
                            <>
                                <PrimaryButton disabled={!cameraActive} className={classes.button} onClick={setCaptured}>Save</PrimaryButton>
                                <PrimaryButton disabled={!cameraActive} className={classes.button} onClick={exitPreview}>Exit</PrimaryButton>
                            </>
                    }

                </Box>
            </div>
        </div>
    );
}

export default ChatAttachmentModal;
